import { defineStore } from 'pinia'
import * as z from 'zod'
import { isEqual } from 'lodash'

export interface SearchParam {
  key: string
  value: any
}

function safeParseJSON(string: string) {
  try { return JSON.parse(string) }
  catch { return string }
}

function makeObjSchema<T extends z.ZodTypeAny>(schema: T) {
  return z.preprocess((input: any) => {
    const parsedObj: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(input)) {
      parsedObj[key] = safeParseJSON(value as string)
    }
    return parsedObj
  }, schema)
}

const stringToArray = (value: string) => {
  if (value.includes(',')) {
    return value.split(',')
  } else {
    return [value]
  }
}

const arrayToString = (value: unknown[]) => {
  return value.join(',')
}

const stringToArrayOfNumbers = (value: string) => {
  return stringToArray(value).map(Number)
}

const arrayKeys = ['member_ids', 'tags']
const paramsSchema = z.object({
  member_ids: z.union([z.string(), z.number()]).transform(v => {
    return typeof v === 'string' ? stringToArrayOfNumbers(v) : [v]
  }).optional(),
  tags: z.union([z.string(), z.number()]).transform(v => {
    return typeof v === 'string' ? stringToArrayOfNumbers(v) : [v]
  }).optional(),
  shared: z.boolean().nullable().optional(),
  has_resources: z.boolean().nullable().optional(),
  text: z.union([z.string(), z.number()]).transform(v => v.toString()).optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  center_lat: z.number().optional(),
  center_lng: z.number().optional(),
  zoom: z.number().optional(),
  south: z.number().optional(),
  north: z.number().optional(),
  east: z.number().optional(),
  west: z.number().optional(),
})

const searchObjSchema = makeObjSchema(paramsSchema)

const applyParam = (params: SearchParam[], urlParams: URLSearchParams, key: string, value: any) => {
  const param = params.find(param => param.key === key)
  if (param) {
    if (Array.isArray(param.value)) {
      param.value.push(value)
      urlParams.set(key, arrayToString(param.value))
    } else {
      param.value = value
      urlParams.set(key, value)
    }
  } else {
    const isArray = arrayKeys.includes(key)
    params.push({ key, value: isArray ? [value] : value })
    urlParams.set(key, value)
  }
}

const stateToParams = (state: SearchParam[]) => {
  const params: Record<string, string> = {}
  state.forEach(param => {
    params[param.key] = param.value.toString()
  })
  return params
}

export const useSearchStore = defineStore('search', {
  state: () => ({
    params: [] as SearchParam[],
  }),

  actions: {
    setParams(params: Record<string, any>) {
      const currentParams = stateToParams(this.params)

      if (isEqual(params, currentParams)) return

      const validatedParams = searchObjSchema.safeParse(params)

      if (validatedParams.success === false) {
        console.log(validatedParams.error)
      } else {
        this.params = []

        Object.entries(validatedParams.data as Record<string, any>).forEach(([key, values]) => {
          const newParam = { key, value: values }
          this.params.push(newParam)
        })
      }
    },
    addParam({ key, value }: SearchParam) {
      const urlParams = new URLSearchParams(window.location.search)
      applyParam(this.params, urlParams, key, value)
      window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`)
    },
    setParamsBulk(entries: SearchParam[]) {
      const urlParams = new URLSearchParams(window.location.search)
      for (const { key, value } of entries) {
        applyParam(this.params, urlParams, key, value)
      }
      window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`)
    },
    removeParam({ key, value }: { key: string, value?: any }) {
      const urlParams = new URLSearchParams(window.location.search)
      const param = this.params.find(param => param.key === key)

      if (!param) return

      if (Array.isArray(param.value)) {
        const index = param.value.indexOf(value)
        if (index > -1) {
          param.value.splice(index, 1)
          urlParams.set(key, arrayToString(param.value))

          if (param.value.length === 0) {
            urlParams.delete(key)
          }
        }
      } else {
        this.params = this.params.filter(param => param.key !== key)
        urlParams.delete(key)
      }
      window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`)
    },
    resetParams() {
      console.log('RESET_PARAMS')
      this.params = []
      window.history.replaceState({}, '', window.location.pathname)
    },
  },
})
